import type { ApolloError } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import type { WithTypeName } from 'src/types';
import * as Schema from 'src/types/graphql';

import {
  updateNextRoleSalary,
  updateNextRoleSalaryFailure,
  updateNextRoleSalarySuccess,
} from '../actionCreators';

import translations from './.vocab';

export const useSalaryPreferences = () => {
  const { locale, language } = useAppConfig();

  const { data, loading, error } = useQuery(
    Schema.GetSalaryPreferencesDocument,
    {
      variables: {
        countryCode: locale,
        languageCode: language,
      },
    },
  );

  const salaryPreferences =
    loading || error ? [] : data?.viewer?.salaryPreferences2 || [];

  return { salaryPreferences, loading, error };
};

const key = 'next-role-salary';

export const useUpdateSalaryPreferences = () => {
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);

  const onMutate = () => {
    dispatch(updateNextRoleSalary());
  };
  const onCompleted = () => {
    dispatch(updateNextRoleSalarySuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateNextRoleSalaryFailure(error));
  };

  return useToastMutation(Schema.UpdateSalaryPreferencesDocument, {
    key,
    errorActionRouteParameters: {
      to: 'salary',
      mode: 'EDIT',
    },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Salary expectation updated'),
  });
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetSalaryPreferencesRequest = (
  salaryPreferences2: Array<WithTypeName<Schema.SalaryPreference2>>,
) => ({
  request: {
    query: Schema.GetSalaryPreferencesDocument,
    variables: { countryCode: 'AU', languageCode: 'en' },
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        salaryPreferences2,
      },
    },
  },
});
